// FicheReviews — avis utilisateurs sur les établissements RÉELS uniquement (v5.15.0).
// Jamais affiché sur les données mock (TEST) — le backend refuse aussi côté API.
import { useEffect, useState } from "react";
import axios from "axios";
import { Star } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const Stars = ({ value, onPick, size = 18 }) => (
  <div style={{ display: "flex", gap: 3 }}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} size={size} style={{ cursor: onPick ? "pointer" : "default" }}
        color={n <= value ? "#EFC75E" : "#3C4F44"} fill={n <= value ? "#EFC75E" : "none"}
        onClick={onPick ? () => onPick(n) : undefined} data-testid={onPick ? `review-star-${n}` : undefined} />
    ))}
  </div>
);

export const FicheReviews = ({ result: r }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", rating: 0, comment: "" });
  const [loading, setLoading] = useState(false);

  const load = () => axios.get(`${API}/api/v3/search/doc/${encodeURIComponent(r.id)}/reviews`)
    .then((res) => setData(res.data)).catch(() => setData({ count: 0, average: null, reviews: [] }));
  useEffect(() => { load(); }, [r.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Votre nom est requis"); return; }
    if (!form.rating) { toast.error("Choisissez une note (1 à 5 étoiles)"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/v3/search/doc/${encodeURIComponent(r.id)}/reviews`, form);
      toast.success("Merci pour votre avis ✓");
      setForm({ name: "", rating: 0, comment: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.error === "reviews_disabled_on_test_data"
        ? "Les avis sont désactivés sur les données de test" : "Échec de l'envoi de l'avis");
    } finally { setLoading(false); }
  };

  return (
    <section style={V.section} data-testid="fiche-reviews">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
        <h2 style={V.h2}>Avis</h2>
        {data?.count > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 7 }} data-testid="reviews-summary">
            <Stars value={Math.round(data.average)} size={14} />
            <span style={{ color: "#EFC75E", fontWeight: 800, fontSize: 13 }}>{data.average}</span>
            <span style={{ color: "#6B7F72", fontSize: 11.5 }}>({data.count} avis)</span>
          </div>
        )}
      </div>

      {/* Formulaire */}
      <div style={{ display: "grid", gap: 8, margin: "10px 0 4px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ color: "#9DB4A6", fontSize: 12 }}>Votre note :</span>
          <Stars value={form.rating} onPick={(n) => setForm((f) => ({ ...f, rating: n }))} />
        </div>
        <input style={V.input} placeholder="Votre nom *" value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="review-name" />
        <textarea style={{ ...V.input, minHeight: 56, resize: "vertical" }} placeholder="Votre commentaire (optionnel)"
          value={form.comment} onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))} data-testid="review-comment" />
        <button onClick={submit} disabled={loading} style={{ ...V.cta, opacity: loading ? 0.6 : 1 }} data-testid="review-submit">
          {loading ? "Envoi…" : "Publier mon avis"}
        </button>
      </div>

      {/* Liste */}
      {data?.reviews?.length > 0 && (
        <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
          {data.reviews.map((rev) => (
            <div key={rev.review_id} style={V.review} data-testid={`review-${rev.review_id}`}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 800, fontSize: 12.5, color: "#ECF5EE" }}>{rev.name}</span>
                <Stars value={rev.rating} size={12} />
              </div>
              {rev.comment && <p style={{ color: "#AFC4B6", fontSize: 12, lineHeight: 1.45, margin: "4px 0 0" }}>{rev.comment}</p>}
              <div style={{ color: "#5E7268", fontSize: 10, marginTop: 3 }}>{new Date(rev.created_at).toLocaleDateString("fr-FR")}</div>
            </div>
          ))}
        </div>
      )}
      {data && data.count === 0 && (
        <div style={{ color: "#6B7F72", fontSize: 11.5, marginTop: 6 }} data-testid="reviews-empty">
          Aucun avis pour le moment — soyez le premier à partager votre expérience.
        </div>
      )}
    </section>
  );
};

const V = {
  section: { marginTop: 16, padding: 13, borderRadius: 14, background: "rgba(8,38,24,0.6)", border: "1px solid rgba(16,185,129,0.18)" },
  h2: { margin: 0, fontSize: 13.5, fontWeight: 800, color: "#ECF5EE" },
  input: { padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(16,185,129,0.3)", background: "rgba(4,18,11,0.6)", color: "#ECF5EE", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" },
  cta: { padding: "9px 16px", borderRadius: 999, border: "1px solid rgba(16,185,129,0.5)", background: "rgba(16,185,129,0.15)", color: "#34D399", fontSize: 12.5, fontWeight: 800, cursor: "pointer", width: "fit-content" },
  review: { padding: "9px 11px", borderRadius: 11, background: "rgba(4,18,11,0.5)", border: "1px solid rgba(16,185,129,0.14)" },
};

export default FicheReviews;
